// Firestore utility for bookings
import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc, 
  updateDoc, 
  setDoc, 
  doc, 
  query, 
  where, 
  Timestamp, 
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';

const BOOKINGS_COLLECTION = 'bookings';

// Add a booking to Firestore
export async function addBookingToFirestore(booking) {
  try {
    console.log('Adding booking to Firestore:', booking);
    
    // Clean the booking data to remove undefined values
    const cleanBookingData = {};
    Object.keys(booking).forEach(key => {
      const value = booking[key];
      if (value !== undefined && value !== null) {
        if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
          // Recursively clean nested objects
          const cleanedNestedObject = {};
          Object.keys(value).forEach(nestedKey => {
            const nestedValue = value[nestedKey];
            if (nestedValue !== undefined && nestedValue !== null) {
              cleanedNestedObject[nestedKey] = nestedValue;
            }
          });
          if (Object.keys(cleanedNestedObject).length > 0) {
            cleanBookingData[key] = cleanedNestedObject;
          }
        } else {
          cleanBookingData[key] = value;
        }
      }
    });
    
    // Ensure createdAt is a Firestore Timestamp
    const bookingData = {
      ...cleanBookingData,
      createdAt: booking.createdAt ? Timestamp.fromDate(new Date(booking.createdAt)) : Timestamp.now(),
    };
    
    console.log('Processed booking data for Firestore:', bookingData);
    const docRef = await addDoc(collection(db, BOOKINGS_COLLECTION), bookingData);
    console.log('Booking successfully added to Firestore with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error adding booking to Firestore:', error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    throw error;
  }
}

// Fetch all bookings
export async function getAllBookingsFromFirestore(forceRefresh = false) {
  try {
    const collectionRef = collection(db, BOOKINGS_COLLECTION);
    const querySnapshot = await getDocs(collectionRef);
    const bookings = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      bookings.push({
        id: doc.id,
        ...data,
        // Convert Firestore Timestamps to ISO strings if they exist
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
        checkIn: data.checkIn || data.bookingData?.start,
        checkOut: data.checkOut || data.bookingData?.end,
      });
    });
    
    console.log(`Fetched bookings from Firestore: ${bookings.length}${forceRefresh ? ' (force refresh)' : ''}`);
    return bookings;
  } catch (error) {
    console.error('Error fetching bookings from Firestore:', error);
    throw error;
  }
}

// Fetch bookings for a user
export async function getUserBookingsFromFirestore(userId) {
  try {
    const q = query(collection(db, BOOKINGS_COLLECTION), where('userId', '==', userId));
    const snapshot = await getDocs(q);
    const bookings = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convert Firestore Timestamp to Date string for consistency
        createdAt: data.createdAt?.toDate?.() || data.createdAt,
        // Ensure guest details are properly structured
        guestName: data.guestName || data.name || 'N/A',
        guestEmail: data.guestEmail || data.email || 'N/A', 
        guestPhone: data.guestPhone || data.phone || 'N/A',
        checkIn: data.checkIn || data.bookingData?.start || 'N/A',
        checkOut: data.checkOut || data.bookingData?.end || 'N/A',
        beds: data.beds || data.bookingData?.beds || 1,
        total: data.total || data.amount || data.bookingData?.total || 0,
        status: data.status || 'Confirmed',
        // CRITICAL: Preserve exact payment status from Firestore, don't override with default
        payment: data.payment || (data.paymentMethod === 'razorpay' ? 'paid' : 'pending')
      };
    });
    console.log('Fetched user bookings from Firestore:', bookings.length);
    return bookings;
  } catch (error) {
    console.error('Error fetching user bookings from Firestore:', error);
    throw error;
  }
}

// Update a booking in Firestore
export const updateBookingInFirestore = async (bookingId, updateData) => {
  try {
    console.log('updateBookingInFirestore: Starting update for booking ID:', bookingId);
    console.log('updateBookingInFirestore: Update data received:', updateData);
    console.log('updateBookingInFirestore: Payment status in update data:', updateData.payment);
    console.log('updateBookingInFirestore: Status in update data:', updateData.status);
    
    const bookingRef = doc(db, 'bookings', bookingId);
    
    // First, get the current document to understand its structure
    const currentDoc = await getDoc(bookingRef);
    if (currentDoc.exists()) {
      const currentData = currentDoc.data();
      console.log('updateBookingInFirestore: Current document structure:', currentData);
      console.log('updateBookingInFirestore: Current payment status:', currentData.payment);
      console.log('updateBookingInFirestore: Current paymentMethod:', currentData.paymentMethod);
      console.log('updateBookingInFirestore: Document source type:', currentData.paymentMethod || 'unknown');
    } else {
      // Document does not exist: perform an UPSERT to create it so edits reflect on live site
      const upsertData = {
        ...updateData,
        id: bookingId,
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      };
      console.log('updateBookingInFirestore: Document missing. Creating via setDoc (upsert):', upsertData);
      await setDoc(bookingRef, upsertData, { merge: true });
      console.log('updateBookingInFirestore: Upsert completed.');
      return upsertData;
    }
    
    // CRITICAL FIX: Ensure payment status is explicitly preserved
    const dataToUpdate = {
      ...updateData,
      updatedAt: new Date().toISOString()
    };
    
    // Force payment status if provided
    if (updateData.payment) {
      dataToUpdate.payment = updateData.payment;
      console.log('updateBookingInFirestore: FORCING payment status to:', updateData.payment);
    }
    
    console.log('updateBookingInFirestore: Final data being sent to Firestore:', dataToUpdate);
    
    // Use merge: true to prevent overwriting other fields
    await updateDoc(bookingRef, dataToUpdate, { merge: true });
    console.log('updateBookingInFirestore: Update completed successfully with merge: true');
    
    // Verify the update by fetching the document
    const updatedDoc = await getDoc(bookingRef);
    if (updatedDoc.exists()) {
      const updatedData = updatedDoc.data();
      console.log('updateBookingInFirestore: Verified payment status:', updatedData.payment);
      console.log('updateBookingInFirestore: Verified status:', updatedData.status);
      console.log('updateBookingInFirestore: Full updated document:', updatedData);
      
      // Check if the payment status actually changed
      if (updateData.payment && updatedData.payment !== updateData.payment) {
        console.error('updateBookingInFirestore: PAYMENT STATUS UPDATE FAILED!');
        console.error('updateBookingInFirestore: Expected:', updateData.payment);
        console.error('updateBookingInFirestore: Actual:', updatedData.payment);
      } else if (updateData.payment) {
        console.log('updateBookingInFirestore: Payment status update SUCCESS!');
      }
    } else {
      console.error('updateBookingInFirestore: Document not found after update');
    }
    
  } catch (error) {
    console.error('updateBookingInFirestore: Error updating booking:', error);
    console.error('updateBookingInFirestore: Error details:', error.message);
    console.error('updateBookingInFirestore: Error code:', error.code);
    
    // Handle case where document doesn't exist in Firestore (localStorage-only booking)
    if (error.code === 'not-found') {
      console.log('updateBookingInFirestore: Document not found in Firestore, checking localStorage...');
      
      // Try to get booking from localStorage
      const localBookings = JSON.parse(localStorage.getItem('bookings') || '[]');
      const localBooking = localBookings.find(booking => booking.id === bookingId);
      
      if (localBooking) {
        console.log('updateBookingInFirestore: Found booking in localStorage, updating locally...');
        
        // Update the booking in localStorage
        const updatedLocalBooking = {
          ...localBooking,
          ...updateData,
          updatedAt: new Date().toISOString()
        };
        
        // Update localStorage
        const updatedBookings = localBookings.map(booking => 
          booking.id === bookingId ? updatedLocalBooking : booking
        );
        localStorage.setItem('bookings', JSON.stringify(updatedBookings));
        
        console.log('updateBookingInFirestore: Successfully updated booking in localStorage');
        return updatedLocalBooking;
      } else {
        console.error('updateBookingInFirestore: Booking not found in localStorage either');
        throw new Error(`Booking ${bookingId} not found in Firestore or localStorage`);
      }
    }
    
    throw error;
  }
};

// Delete a booking from Firestore
export async function deleteBookingFromFirestore(bookingId) {
  console.log('🗑️ Firestore deletion - Starting for booking ID:', bookingId);
  
  try {
    console.log('🗑️ Attempting direct document deletion...');
    // Try direct document deletion first
    const docRef = doc(db, BOOKINGS_COLLECTION, bookingId);
    await deleteDoc(docRef);
    console.log('✅ Booking deleted successfully from Firestore (direct method)');
    
    // Verify deletion by trying to fetch the document
    await verifyDeletion(bookingId);
    return true;
  } catch (directError) {
    console.log('⚠️ Direct deletion failed, trying query method...', directError.message);
    
    try {
      // Fallback: Query by 'id' field
      console.log('🗑️ Querying by id field...');
      const q = query(collection(db, BOOKINGS_COLLECTION), where('id', '==', bookingId));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        console.log('🗑️ Found booking via id query, deleting...');
        const docRef = snapshot.docs[0].ref;
        await deleteDoc(docRef);
        console.log('✅ Booking deleted successfully from Firestore (id query method)');
        await verifyDeletion(bookingId);
        return true;
      }
      
      console.log('🗑️ Booking not found via id query, trying bookingId field...');
      // Fallback: Query by 'bookingId' field
      const q2 = query(collection(db, BOOKINGS_COLLECTION), where('bookingId', '==', bookingId));
      const snapshot2 = await getDocs(q2);
      
      if (!snapshot2.empty) {
        console.log('🗑️ Found booking via bookingId query, deleting...');
        const docRef = snapshot2.docs[0].ref;
        await deleteDoc(docRef);
        console.log('✅ Booking deleted successfully from Firestore (bookingId query method)');
        await verifyDeletion(bookingId);
        return true;
      }
      
      console.log('🗑️ Trying to find booking by any field containing the ID...');
      // Last resort: Get all bookings and find by any matching field
      const allBookingsSnapshot = await getDocs(collection(db, BOOKINGS_COLLECTION));
      let foundDoc = null;
      
      allBookingsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (doc.id === bookingId || 
            data.id === bookingId || 
            data.bookingId === bookingId ||
            data.razorpayOrderId === bookingId) {
          foundDoc = doc;
        }
      });
      
      if (foundDoc) {
        console.log('🗑️ Found booking via comprehensive search, deleting...');
        await deleteDoc(foundDoc.ref);
        console.log('✅ Booking deleted successfully from Firestore (comprehensive search method)');
        await verifyDeletion(bookingId);
        return true;
      }
      
      console.error('❌ Booking not found in Firestore with ID:', bookingId);
      throw new Error(`Booking with ID ${bookingId} not found in Firestore`);
      
    } catch (queryError) {
      console.error('❌ Query deletion failed:', queryError);
      throw queryError;
    }
  }
}

// Verify that the booking was actually deleted
async function verifyDeletion(bookingId) {
  console.log('🔍 Verifying deletion for booking ID:', bookingId);
  
  try {
    // Wait a moment for Firestore consistency
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Try to fetch the document directly
    const docRef = doc(db, BOOKINGS_COLLECTION, bookingId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      console.error('❌ Verification failed: Document still exists after deletion');
      throw new Error('Document still exists after deletion');
    }
    
    // Also check via query to be thorough
    const q = query(collection(db, BOOKINGS_COLLECTION), where('id', '==', bookingId));
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      console.error('❌ Verification failed: Document found via query after deletion');
      throw new Error('Document found via query after deletion');
    }
    
    console.log('✅ Deletion verified: Document no longer exists in Firestore');
  } catch (error) {
    console.error('❌ Deletion verification failed:', error);
    throw error;
  }
}
